import * as tslib_1 from "tslib";
/* tslint:disable:forbid-import */
import { autorun, computed } from 'mobx';
/* tslint:enable:forbid-import */
import { attachmentStore } from 'owa-attachment-model-store';
import { getAttachmentUrlForClassic } from 'owa-attachment-url';
import { getPalette } from 'owa-theme';
import { trace } from 'owa-trace';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { select } from 'satcheljs/lib/legacy';
import { lazyGetImageDimensionsFromFile, lazySetInlineAttachmentStatus, } from 'owa-attachment-well-data';
import { lazyCacheInlineImage, lazyClearInlineImageStore, lazyLogImageUpload, lazyLogImageLoadError, lazyLoadFromAttachment, lazyLoadFromAttachmentServiceUrl, } from 'owa-inline-image-loader';
import { AttachmentFileType, lazyCreateLocalComputerFiles, } from 'owa-attachment-common';
import { cleanupImageCopyHandler, handleImageCopy, } from 'owa-inline-image-loader/lib/utils/ImageCopyHandler';
var ADD_INLINE_IMAGE_TO_STORE_IE_TIMEOUT_LENGTH = 1000;
var PASTED_IMAGE_NAME_PREFIX = 'pastedImage';
var PASTED_IMAGE_NAME_SUFFIX = '.png';
var PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME = 'data-loadPending';
// Inline image CID fix
var CID_PREFIX = 'cid:';
// The data-custom attribute name
var ATTRIBUTE_DATACUSTOM = 'data-custom';
// The content change source for addins
var ADDIN_CONTENT_CHANGE_SOURCE = 'Addins';
// The content change source for images moved from attachment well to message body
// Must be kept in sync with const in insertImageFromExistingInlineAttachment.ts
var MOVEATTACHMENTTOINLINE_CHANGE_SOURCE = 'MoveAttachmentToInline';
// The content change source for SetContent
var SETCONTENT_CONTENT_CHANGE_SOURCE = 'SetContent';
// The context used for inline image upload
var CONTEXT_UPLOAD = 'Upload';
// The context used for inline image load (load from existing content, draft, forward etc.)
var CONTEXT_COMPOSE = 'Compose';
// The context used for inline image load by moving from attachment well
var CONTEXT_MOVEFROMATTACHMENTWELL = 'MoveFromAttachmentWell';
// File type
var FILETYPE_ATTACHMENT = 'Attachment';
// Default place holder dimension
var DEFAULT_PLACEHOLDER_WIDTH = 320;
var DEFAULT_PLACEHOLDER_HEIGHT = 220;
// Error
var ERROR_UNSUPPORTED_FILETYPE = 'UnsupportedFileType';
var ERROR_NULL_ATTACHMENT_URL = 'NullAttachmentUrl';
// Paste image
var SAFARI_IMAGE_BLOB_PREFIX = 'blob:';
var WEBKIT_FAKE_URL_IMAGE_PREFIX = 'webkit-fake-url:';
var LOCAL_FILE_REFERENCE_REGEX = new RegExp('^file:///[a-zA-Z]:', 'i');
var BASE64_IMAGE_REGEX = new RegExp('^data:(image\\/\\w+);base64,([A-Za-z0-9+/=]+?)$', 'i');
var ImageStatus;
(function (ImageStatus) {
    ImageStatus[ImageStatus["Started"] = 0] = "Started";
    ImageStatus[ImageStatus["PlaceholderShown"] = 1] = "PlaceholderShown";
})(ImageStatus || (ImageStatus = {}));
var INSERT_OPTION = {
    position: 2 /* SelectionStart */,
    updateCursor: true,
    replaceSelection: true,
    insertOnNewLine: false,
};
/*
 * Notes: all potential calling orders are listed below.
 * These functions in the class are called asyncly:
 * A: insertPlaceholderImage called on get image dimension callback, in the error case it return dimensions as 0 * 0
 * B: processInlineImageAfterUploadSuccess called from createattachment response success
 * B': onImageError called from createattachment response failure (C/C' will not happen in this case)
 * C: onImageLoad called from actual image onload
 * C': onImageError called from actual image onerror
 * C/C' is bound to happen after B, but the order of A and B/C is uncertain
 *
 * So we have potential orders as below (Please refer to InlineImagePluginTest for expected results):
 * 1: A, B, C (placeholder was inserted before createattachment succeeded)
 * 2: A, B' (placeholder was inserted before createattachment failed)
 * 3: A, B, C' (placeholder was inserted before createattachment succeeded, but actual image failed to load)
 *
 * 4. B, C, A (createattachment succeeded before placeholder was inserted)
 * 5. B, C', A (createattachment succeeded before placeholder was inserted, but actual image failed to load)
 * 6. B', A (createattachment failed before placeholder was inserted)
 *
 * 7. B, A, C (createattachment succeeded before placeholder was inserted, but actual image was loaded after)
 * 8. B, A, C' (createattachment succeeded before placeholder was inserted, but actual image was loaded after and failed)
 *
 * Also if A happens before B/C, placeholder can be removed by user, call it -A.
 * Note that -A can not happen after B', C, C'.
 * So additional potential orders are
 * 9. A, -A, B, C/C' (placeholder was inserted then deleted by user before createattachment succeeded, then actual image was loaded/failed to load)
 * 10. A, -A, B' (placeholder was inserted then deleted by user before createattachment failed)
 * 11. A, B, -A, C/C' (placeholder was inserted before createattachment succeeded, then deleted by user before actual image was loaded/failed to load)
 * 12. B, A, -A, C/C' (createattachment succeeded, then placeholder was inserted and deleted by user before actual image was loaded/failed to load)
 */
var InlineImagePlugin = /** @class */ (function () {
    function InlineImagePlugin(getInlineAttachmentStates, createAttachments, deleteInlineAttachments, convertContentIdToAttachmentUrl, state) {
        var _this = this;
        this.getInlineAttachmentStates = getInlineAttachmentStates;
        this.createAttachments = createAttachments;
        this.deleteInlineAttachments = deleteInlineAttachments;
        this.convertContentIdToAttachmentUrl = convertContentIdToAttachmentUrl;
        this.name = 'InlineImage';
        this.processInlineImageAfterUploadSuccess = function (attachmentId, temporaryId, state) {
            if (!_this.editor) {
                // This function is called asyncly. This instance may have been disposed.
                return;
            }
            var attachment = state.attachmentStore.get(attachmentId.Id).model;
            var downloadUrl = getAttachmentUrlForClassic(attachmentId, 1 /* Preview */);
            if (downloadUrl) {
                _this.replacePlaceholderImage(temporaryId, downloadUrl, attachment.ContentId, attachment.Size, attachment.ContentType);
            }
            else {
                lazyLogImageLoadError
                    .import()
                    .then(function (imageLoadErrorLogger) {
                    return imageLoadErrorLogger(FILETYPE_ATTACHMENT /* type */, '' /* howLoad */, ERROR_NULL_ATTACHMENT_URL /* error */, '' /* statusCode */, CONTEXT_UPLOAD /* context */);
                });
            }
        };
        this.onCopy = function (event) {
            handleImageCopy(event.currentTarget);
        };
        // Process the mutation record looking for placeholder images that have been deleted
        this.processNodeDeleted = function (nodes) {
            for (var i = 0; i < nodes.length; i++) {
                var removedNodes = nodes[i].removedNodes;
                if (removedNodes) {
                    for (var j = 0; j < removedNodes.length; j++) {
                        var removedNode = removedNodes.item(j);
                        // Check the removed node to see if it is a placeholder that is still pending load
                        var temporaryIdForRemovedNode = _this.getPendingPlaceholderForNode(removedNode);
                        if (temporaryIdForRemovedNode) {
                            trace.info('InlineImagePlugin: Pending image placeholder removed from DOM. Deleting the attachment.');
                            _this.deleteInlineAttachments(temporaryIdForRemovedNode);
                            if (_this.imageStatusMap[temporaryIdForRemovedNode]) {
                                delete _this.imageStatusMap[temporaryIdForRemovedNode];
                            }
                        }
                    }
                }
            }
            _this.disconnectMutationObserverIfNecessary();
        };
        // InlineImageStore keys dataURIs by originalsrc. Since originalsrc is only guaranteed to be unique within a message,
        // clear the store to avoid any false positives.
        lazyClearInlineImageStore.importAndExecute();
        this.processInlineImageAfterUploadSuccess = select({
            attachmentStore: function () { return attachmentStore.attachments; },
        })(this.processInlineImageAfterUploadSuccess);
    }
    InlineImagePlugin.prototype.onPluginEvent = function (event) {
        if (event.eventType == 8 /* BeforePaste */) {
            this.processInlineImage(event);
        }
        else if (event.eventType == 6 /* ContentChanged */) {
            var eventSource = event.source;
            if (eventSource == ADDIN_CONTENT_CHANGE_SOURCE ||
                eventSource == MOVEATTACHMENTTOINLINE_CHANGE_SOURCE) {
                this.convertAllContentIdToAttachmentUrl();
            }
            else if (eventSource == SETCONTENT_CONTENT_CHANGE_SOURCE) {
                this.loadInlineImages();
            }
        }
    };
    InlineImagePlugin.prototype.initialize = function (editor) {
        var _this = this;
        this.editor = editor;
        this.imageStatusMap = {};
        var inlineAttachments = this.getInlineAttachmentStates();
        var inlineAttachmentStatusComputed = computed(function () {
            return inlineAttachments
                .filter(function (inlineAttachment) { return !inlineAttachment.isHiddenInline; })
                .map(function (inlineAttachment) { return inlineAttachment.status; });
        });
        this.onCopyDisposer = this.editor.addDomEventHandler('copy', this.onCopy);
        this.observer = new MutationObserver(this.processNodeDeleted);
        this.autorunDisposer = autorun(function () {
            var computed = inlineAttachmentStatusComputed.get();
            computed.forEach(function (status, index) {
                if (status == 1 /* CreateAttachmentSucceeded */ ||
                    status == 2 /* CreateAttachmentFailed */) {
                    var attachment = inlineAttachments[index];
                    if (status == 1 /* CreateAttachmentSucceeded */) {
                        _this.processInlineImageAfterUploadSuccess(attachment.attachmentId, attachment.placeholderId);
                    }
                    else {
                        _this.onImageError(null /* imageUrl */, attachment.placeholderId, null /* TargetElement */);
                    }
                    // Complete the inline attachment.
                    lazySetInlineAttachmentStatus.importAndExecute(attachment, 3 /* Completed */);
                }
            });
        });
    };
    InlineImagePlugin.prototype.dispose = function () {
        this.editor = null;
        this.observer.disconnect();
        this.imageStatusMap = null;
        this.autorunDisposer();
        cleanupImageCopyHandler();
        if (this.onCopyDisposer) {
            this.onCopyDisposer();
            this.onCopyDisposer = null;
        }
    };
    InlineImagePlugin.prototype.processInlineImageBeforeUpload = function (temporaryId, file) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            var fileObject;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // The editor or inline image plugin could already have been disposed by the time this callback is executed
                        if (!this.editor || !this.imageStatusMap) {
                            return [2 /*return*/];
                        }
                        lazyLogImageUpload
                            .import()
                            .then(function (imageUploadLogger) { return imageUploadLogger(file.fileType.toString()); });
                        if (!(file.fileType == AttachmentFileType.Base64InlineImage)) return [3 /*break*/, 1];
                        this.imageStatusMap[temporaryId] = {
                            status: ImageStatus.PlaceholderShown,
                            placeholder: file.imageNode,
                        };
                        return [3 /*break*/, 4];
                    case 1:
                        if (!(file.fileType == AttachmentFileType.Local)) return [3 /*break*/, 3];
                        fileObject = file.fileObject;
                        this.imageStatusMap[temporaryId] = { status: ImageStatus.Started };
                        return [4 /*yield*/, lazyGetImageDimensionsFromFile.import()];
                    case 2:
                        (_a.sent())(fileObject, function (height, width) {
                            return _this.insertPlaceholderImage(temporaryId, file.name, height, width);
                        });
                        return [3 /*break*/, 4];
                    case 3:
                        lazyLogImageLoadError
                            .import()
                            .then(function (imageLoadErrorLogger) {
                            return imageLoadErrorLogger(file.fileType.toString() /* type */, '' /* howLoad */, ERROR_UNSUPPORTED_FILETYPE /* error */, '' /* statusCode */, CONTEXT_UPLOAD /* context */);
                        });
                        _a.label = 4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    InlineImagePlugin.prototype.insertPlaceholderImage = function (temporaryId, fileName, placeholderImageWidth, placeholderImageHeight) {
        if (!this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }
        var entry = this.imageStatusMap[temporaryId];
        if (entry && entry.status == ImageStatus.Started) {
            this.editor.addUndoSnapshot();
            var placeholderNode = this.generatePlaceholderNode(temporaryId, fileName, placeholderImageWidth, placeholderImageHeight);
            this.editor.insertNode(placeholderNode, INSERT_OPTION);
            var actualWidth = placeholderNode.offsetWidth;
            if (actualWidth < placeholderImageWidth) {
                // Resize placeholder node proportionally.
                placeholderNode.style.height = Math.ceil(actualWidth / placeholderImageWidth * placeholderImageHeight).toString();
            }
            entry.placeholder = placeholderNode;
            entry.status = ImageStatus.PlaceholderShown;
            // Start observing on the parent for the placeholder node. This is so that we will be notified when the node is removed
            // so we can cleanup the pending upload. Note: according to the documentaion for MutationObserver calling .observe multiple
            // times on with same element has no effect.
            var contentEditableParent = this.getContentEditableParent(placeholderNode);
            if (contentEditableParent) {
                trace.info('InlineImagePlugin: Start observing on content editable div');
                this.observer.observe(contentEditableParent, { subtree: true, childList: true });
            }
        }
    };
    InlineImagePlugin.prototype.onImageLoad = function (temporaryId, targetElement) {
        if (!this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }
        // Disable native image selection on the image element
        targetElement.setAttribute('unselectable', 'on');
        targetElement.style.msUserSelect = 'none';
        targetElement.style.webkitUserSelect = 'none';
        var entry = this.imageStatusMap[temporaryId];
        if (entry && entry.status == ImageStatus.PlaceholderShown) {
            // If placeholder has been inserted
            var sourceElement = entry.placeholder;
            if (this.editor.contains(sourceElement)) {
                // If placeholder is found, replace it.
                sourceElement.removeAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME);
                this.editor.replaceNode(sourceElement, targetElement);
                // After replacing the node to insert the image, move the insertion point so its immediately after the
                // newly inserted image
                var selection = this.editor.getSelectionRange();
                if (selection) {
                    selection.selectNode(targetElement);
                    selection.collapse(false);
                    this.editor.updateSelection(selection);
                }
            }
            else {
                // If placeholder is not found, i.e. user has deleted it
                // Directly insert it at cursor.
                this.editor.insertNode(targetElement, INSERT_OPTION);
            }
        }
        else {
            // If placeholder has not been inserted, directly insert at cursor.
            this.editor.insertNode(targetElement, INSERT_OPTION);
        }
        // Generate and store a dataURI for the copy handler and the reading pane's local lie scenario
        if (isBrowserIE()) {
            // IE triggers onload for images before the image is actually in the DOM
            // so we need to call it from a timer to let the image finish loading.
            setTimeout(function () {
                lazyCacheInlineImage.import().then(function (cacher) { return cacher(targetElement); });
            }, ADD_INLINE_IMAGE_TO_STORE_IE_TIMEOUT_LENGTH);
        }
        else {
            lazyCacheInlineImage.import().then(function (cacher) { return cacher(targetElement); });
        }
        delete this.imageStatusMap[temporaryId];
        this.addEditorUndoSnapshotIfNecessary();
        this.disconnectMutationObserverIfNecessary();
        targetElement.onload = null;
    };
    InlineImagePlugin.prototype.onImageError = function (imageUrl, temporaryId, targetElement) {
        if (!this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }
        var entry = this.imageStatusMap[temporaryId];
        if (entry && entry.status == ImageStatus.PlaceholderShown) {
            // If placeholder has been inserted
            var sourceElement = entry.placeholder;
            if (sourceElement) {
                // If placeholder is found. remove it on error.
                this.editor.deleteNode(sourceElement);
            }
        }
        delete this.imageStatusMap[temporaryId];
        this.addEditorUndoSnapshotIfNecessary();
    };
    InlineImagePlugin.prototype.processInlineImage = function (event) {
        if (event.pasteOption == 0 /* PasteHtml */) {
            var images = event.fragment.querySelectorAll('img');
            var imageCount = images.length;
            var base64InlineImageFiles = [];
            var base64FileIndex = 0;
            for (var i = 0; i < imageCount; i++) {
                var image = images[i];
                var imageSrc = image.getAttribute('src');
                // Check for images with no src or images pasted from Safari which don't have usable sources
                if (!imageSrc ||
                    imageSrc.indexOf(SAFARI_IMAGE_BLOB_PREFIX) === 0 ||
                    imageSrc.indexOf(WEBKIT_FAKE_URL_IMAGE_PREFIX) === 0 ||
                    imageSrc.match(LOCAL_FILE_REFERENCE_REGEX)) {
                    // Remove all images with no src or local file src
                    image.parentNode.removeChild(image);
                    continue;
                }
                // Check for images with src as base64 data uri
                var base64Match = imageSrc.match(BASE64_IMAGE_REGEX);
                if (base64Match && base64Match.length === 3) {
                    var base64InlineImageFile = {
                        name: PASTED_IMAGE_NAME_PREFIX +
                            'base64' +
                            base64FileIndex++ +
                            PASTED_IMAGE_NAME_SUFFIX,
                        size: 0,
                        type: base64Match[1],
                        dataUri: base64Match[2],
                        imageNode: image,
                        fileType: AttachmentFileType.Base64InlineImage,
                    };
                    base64InlineImageFiles.push(base64InlineImageFile);
                }
            }
            if (base64InlineImageFiles.length > 0) {
                this.createAttachments(base64InlineImageFiles, true /* isInline */, false /* shouldShare */, false /* isHiddenInline */, this.processInlineImageBeforeUpload.bind(this));
            }
        }
        else if (event.pasteOption == 2 /* PasteImage */) {
            while (event.fragment.firstChild) {
                event.fragment.removeChild(event.fragment.firstChild);
            }
            event.pasteOption = 0 /* PasteHtml */;
            this.createAttachmentsForLocalFiles(event.clipboardData.image);
        }
    };
    InlineImagePlugin.prototype.replacePlaceholderImage = function (temporaryId, imageUrl, contentId, size, contentType) {
        var _this = this;
        var targetElement = this.editor.getDocument().createElement('img');
        targetElement.setAttribute('originalSrc', 'cid:' + contentId);
        targetElement.setAttribute('size', size.toString());
        targetElement.setAttribute('contentType', contentType);
        targetElement.style.maxWidth = '100%';
        lazyLoadFromAttachmentServiceUrl.import().then(function (logger) {
            return logger(targetElement, imageUrl, CONTEXT_UPLOAD, function (imageElement) {
                _this.onImageLoad(temporaryId, imageElement);
            }, function (imageElement) {
                _this.onImageError(imageUrl, temporaryId, imageElement);
            });
        });
    };
    InlineImagePlugin.prototype.generatePlaceholderNode = function (temporaryId, fileName, placeholderWidth, placeholderHeight) {
        var placeholderDiv = this.editor.getDocument().createElement('div');
        var style = placeholderDiv.style;
        if (placeholderWidth == 0 || placeholderHeight == 0) {
            placeholderWidth = DEFAULT_PLACEHOLDER_WIDTH;
            placeholderHeight = DEFAULT_PLACEHOLDER_HEIGHT;
        }
        style.display = 'inline-block';
        style.maxWidth = '100%';
        style.borderWidth = '1px';
        style.borderStyle = 'dashed';
        style.borderColor = getPalette().themePrimary;
        style.boxSizing = 'border-box';
        style.width = placeholderWidth.toString() + 'px';
        style.height = placeholderHeight.toString() + 'px';
        placeholderDiv.title = fileName;
        placeholderDiv.contentEditable = 'false';
        placeholderDiv.setAttribute('tempid', temporaryId);
        placeholderDiv.setAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME, 'true');
        return placeholderDiv;
    };
    InlineImagePlugin.prototype.createAttachmentsForLocalFiles = function (file) {
        if (isBrowserIE()) {
            // IE doesn't allow loading a script with src as protocol-relative url in onpaste handler
            // so we need to call it from a timer in order to lazy load owa.AttachmentsCommon.js from lazyCreateLocalComputerFiles.import()
            var self_1 = this;
            setTimeout(function () {
                self_1.createAttachmentsForLocalFilesHelper(file);
            }, 0);
        }
        else {
            this.createAttachmentsForLocalFilesHelper(file);
        }
    };
    InlineImagePlugin.prototype.createAttachmentsForLocalFilesHelper = function (file) {
        var _this = this;
        lazyCreateLocalComputerFiles.import().then(function (createLocalComputerFiles) {
            var localComputerFile = createLocalComputerFiles([file])[0];
            if (!localComputerFile.name) {
                localComputerFile.name =
                    PASTED_IMAGE_NAME_PREFIX + 'file' + PASTED_IMAGE_NAME_SUFFIX;
            }
            _this.createAttachments([localComputerFile], true /* isInline */, false /* shouldShare */, false /* isHiddenInline */, _this.processInlineImageBeforeUpload.bind(_this));
        });
    };
    InlineImagePlugin.prototype.addEditorUndoSnapshotIfNecessary = function () {
        if (Object.keys(this.imageStatusMap).length == 0) {
            // if there're pending inline attachments (either started or placeholder shown),
            // do not add editor undo snapshot yet,
            // add it when the last pending inline attachment is done
            // broadcast contentChangedEvent to ensure the snapshot actually gets added
            this.editor.triggerContentChangedEvent('InlineImage');
            this.editor.addUndoSnapshot();
        }
    };
    InlineImagePlugin.prototype.disconnectMutationObserverIfNecessary = function () {
        // If there aren't any pending inline attachments then we don't need the mutation observer to
        // listen for anything. It will start observing again if a new placeholder is added
        if (Object.keys(this.imageStatusMap).length === 0) {
            trace.info('InlineImagePlugin: Disconnect mutation observer');
            this.observer.disconnect();
        }
    };
    // Gets the nearest parent element for the given element with the contenteditable attribute set to true
    InlineImagePlugin.prototype.getContentEditableParent = function (element) {
        while (element) {
            if (element.contentEditable == 'true') {
                return element;
            }
            element = element.parentElement;
        }
        return null;
    };
    // Checks to see if the given node is a pending placeholder, and it if is return its tempid.
    // Otherwise recursively check all of its children to find a pending placholder.
    InlineImagePlugin.prototype.getPendingPlaceholderForNode = function (node) {
        if (node.nodeName.toLowerCase() == 'div' &&
            node.hasAttribute('tempid') &&
            node.hasAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME)) {
            return node.getAttribute('tempid');
        }
        else if (node.hasChildNodes()) {
            for (var i = 0; i < node.childNodes.length; i++) {
                var tempid = this.getPendingPlaceholderForNode(node.childNodes[i]);
                if (tempid) {
                    return tempid;
                }
            }
        }
        return null;
    };
    InlineImagePlugin.prototype.convertAllContentIdToAttachmentUrl = function () {
        var images = this.editor.queryContent("img[src^=\"" + CID_PREFIX + "\"]");
        var _loop_1 = function (i) {
            var image = images[i];
            var cidOrName = image.src.substr(CID_PREFIX.length);
            var imageAttributes = this_1.convertContentIdToAttachmentUrl(cidOrName);
            if (imageAttributes) {
                image.setAttribute('originalsrc', CID_PREFIX + imageAttributes.contentId);
                lazyLoadFromAttachmentServiceUrl
                    .import()
                    .then(function (logger) {
                    return logger(image, imageAttributes.url, CONTEXT_MOVEFROMATTACHMENTWELL);
                });
            }
        };
        var this_1 = this;
        for (var i = 0; i < images.length; i++) {
            _loop_1(i);
        }
    };
    InlineImagePlugin.prototype.loadInlineImages = function () {
        var images = this.editor.queryContent("img[" + ATTRIBUTE_DATACUSTOM + "]");
        if (images && images.length > 0) {
            var _loop_2 = function (i) {
                var image = images[i];
                lazyLoadFromAttachment
                    .import()
                    .then(function (loader) { return loader(image, CONTEXT_COMPOSE, false /* usePlaceHolder */); });
            };
            for (var i = 0; i < images.length; i++) {
                _loop_2(i);
            }
        }
    };
    return InlineImagePlugin;
}());
export default InlineImagePlugin;
//# sourceMappingURL=InlineImagePlugin.js.map